import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';

import { updateShouldReloadUponNavigation } from '@sorare/routing';

import { useFeatureFlags } from 'hooks/useFeatureFlags';
import { useLocationChanged } from 'hooks/useLocationChanged';
import { loadCookie, saveCookie } from 'lib/cookies';

import { isOutdated } from './isOutdated';
import { reloadPage } from './reloadPage';
import { ORIGIN, VERSION } from '../../config';

const recentlyUpdatedCookieName = '_sorare_recently_updated';

const fetchVersion = async () => {
  try {
    const res = await fetch(ORIGIN, { cache: 'no-store' });
    const text = await res.text();
    const match = text.match(/data-sorare-version="([^"]+)"/);
    const version = match?.[1];
    updateShouldReloadUponNavigation(!!isOutdated(version));
  } catch {
    updateShouldReloadUponNavigation(false);
  }
};

const ReloadPage = ({
  minimumFrontendVersion,
}: {
  minimumFrontendVersion: string;
}) => {
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.info(
      `Outdated version (${VERSION} < ${minimumFrontendVersion}), reloading...`
    );
    reloadPage(minimumFrontendVersion);
    saveCookie(recentlyUpdatedCookieName, `${Date.now()}`, {
      maxAge: 120 + Math.floor(Math.random() * 180),
      path: '/',
    });
  }, [minimumFrontendVersion]);
  return null;
};

export const EnsureLatestAppVersion = () => {
  const location = useLocation();
  const locationRef = useRef(location.pathname);
  /** locationChanged doesn't work in a useEffect since it will always be true when changed once */
  const locationChanged = useLocationChanged();
  const {
    flags: { minimumFrontendVersion, useForceRefreshUponNavigation },
  } = useFeatureFlags();

  useEffect(() => {
    if (
      useForceRefreshUponNavigation &&
      locationRef.current !== location.pathname &&
      !loadCookie(recentlyUpdatedCookieName)
    ) {
      locationRef.current = location.pathname;
      saveCookie(recentlyUpdatedCookieName, `${Date.now()}`, {
        maxAge: 300 + Math.floor(Math.random() * 300),
        path: '/',
      });
      fetchVersion();
    }
  });

  if (
    !locationChanged ||
    !isOutdated(minimumFrontendVersion) ||
    useForceRefreshUponNavigation
  ) {
    return null;
  }

  // loop protection, if we do not meet the minimum version we will update every 2-5 minute till we reach it
  if (loadCookie(recentlyUpdatedCookieName)) {
    // eslint-disable-next-line no-console
    console.info(
      `Outdated version (${VERSION} < ${minimumFrontendVersion}), cookie avoiding reload.`
    );
    return null;
  }

  return <ReloadPage minimumFrontendVersion={minimumFrontendVersion} />;
};
